"use client";

import React, { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { CountryCodeSelector } from "./CountryCodeSelector";
import { COUNTRY_CODES, CountryCode } from "@/data/countryCodes";
import { getPhoneValidationRules } from "@/utils/phoneValidation";

interface PhoneInputProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  error?: string;
  className?: string;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  error,
  className,
}) => {
  // Normalize countryCode to ensure it has + prefix
  const normalizedCountryCode = useMemo(() => {
    if (!countryCode) return "+33" as CountryCode;
    return countryCode.startsWith("+") ? (countryCode as CountryCode) : (`+${countryCode}` as CountryCode);
  }, [countryCode]);

  // Get expected phone number length for the selected country
  const validationRules = useMemo(() => {
    return getPhoneValidationRules(normalizedCountryCode);
  }, [normalizedCountryCode]);

  const handleCountryCodeChange = (code: CountryCode) => {
    onCountryCodeChange(code);
  };

  // Handle phone number input with max length blocking and letter filtering
  const handlePhoneNumberChange = (value: string) => {
    // Remove letters - only allow digits and common phone formatting characters (spaces, dashes, parentheses)
    const filteredValue = value.replace(/[^\d\s\-\(\)]/g, '');
    
    if (!validationRules) {
      // If no validation rules, allow filtered input
      onPhoneNumberChange(filteredValue);
      return;
    }

    // Extract only digits from input
    const newDigitsOnly = filteredValue.replace(/\D/g, '');
    const currentDigitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Check if user is trying to add more digits when already at max
    const isAdding = newDigitsOnly.length > currentDigitsOnly.length;
    const exceedsMax = newDigitsOnly.length > validationRules.maxLength;
    
    // Block input if trying to add digits beyond max length
    if (isAdding && exceedsMax) {
      // Don't update - block the input
      return;
    }
    
    // Allow deletion and normal input within limits (with filtered characters)
    onPhoneNumberChange(filteredValue);
  };

  // Count digits in phone number for real-time feedback
  const digitCount = phoneNumber.replace(/\D/g, '').length;
  const isValidLength = validationRules 
    ? digitCount >= validationRules.minLength && digitCount <= validationRules.maxLength
    : null;
  const isAtMaxLength = validationRules ? digitCount >= validationRules.maxLength : false;

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex gap-2">
        <CountryCodeSelector
          value={normalizedCountryCode}
          onChange={handleCountryCodeChange}
          countryList={COUNTRY_CODES}
          className={cn("bg-[#FAFAF9] w-[140px] sm:w-[160px]", error && "border-red-500")}
          error={!!error}
        />
        <div className="flex-1 relative">
          <Input
            type="tel"
            value={phoneNumber}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            onKeyDown={(e) => {
              // Block letters and special characters (only allow digits and formatting chars)
              if (!['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(e.key)) {
                // Allow digits, spaces, dashes, parentheses
                if (!/[\d\s\-\(\)]/.test(e.key) || /[a-zA-Z]/.test(e.key)) {
                  e.preventDefault();
                  return;
                }
              }
              
              // Block typing when at max length (but allow deletion keys)
              if (isAtMaxLength && 
                  digitCount >= (validationRules?.maxLength || Infinity) &&
                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(e.key) &&
                  /\d/.test(e.key)) {
                e.preventDefault();
              }
            }}
            onPaste={(e) => {
              // Handle paste - filter out letters
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const filtered = pastedText.replace(/[^\d\s\-\(\)]/g, '');
              if (filtered) {
                handlePhoneNumberChange(phoneNumber + filtered);
              }
            }}
            placeholder="123 4567"
            className={cn(
              "flex-1",
              error && "border-red-500",
              !error && phoneNumber && isValidLength === true && "border-green-500"
            )}
          />
          {validationRules && phoneNumber && !error && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              {digitCount}/{validationRules.minLength === validationRules.maxLength 
                ? validationRules.minLength 
                : `${validationRules.minLength}-${validationRules.maxLength}`}
            </div>
          )}
        </div>
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
      {!error && validationRules && phoneNumber && (
        <p className="text-xs text-gray-500">
          {isValidLength 
            ? `✓ Valid length (${validationRules.minLength === validationRules.maxLength 
                ? `${validationRules.minLength} digits required` 
                : `${validationRules.minLength}-${validationRules.maxLength} digits required`})`
            : `Enter ${validationRules.minLength === validationRules.maxLength 
                ? `${validationRules.minLength} digits` 
                : `${validationRules.minLength}-${validationRules.maxLength} digits`} for this country`
          }
        </p>
      )}
    </div>
  );
};

export default PhoneInput;

